
import React, { Suspense, useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useParams, Outlet, useLocation, Link } from 'react-router-dom';
import { LANGUAGES, TRANSLATIONS } from '../constants';
import { LanguageCode } from '../types';
import { Menu, X, Globe, ChevronDown, Github, Twitter, Linkedin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export const Layout: React.FC = () => {
  const { lang = 'en' } = useParams<{ lang: LanguageCode }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = LANGUAGES[lang as LanguageCode] ? (lang as LanguageCode) : 'en';
  const t = TRANSLATIONS[currentLang];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [hideMobileChrome, setHideMobileChrome] = useState(false);
  const [showMobileQR, setShowMobileQR] = useState(false);
  const lastScrollY = useRef(0);
  const mobileQrUrl = typeof window !== 'undefined' ? window.location.origin : 'https://yesqrcode.com';

  const handleLangChange = (code: LanguageCode) => {
    const pathParts = location.pathname.split('/');
    pathParts[1] = code;
    navigate(pathParts.join('/'));
    setIsLangOpen(false);
    setIsMenuOpen(false);
  };

  const generatorPaths = ['/qrcode-generator', '/text-to-qrcode', '/link-to-qrcode'];
  const isGeneratorRoute = generatorPaths.some((segment) => location.pathname.includes(segment));
  const isScannerRoute = location.pathname.includes('/qrcode-scanner');

  const navClass = (active: boolean) =>
    `text-base font-semibold transition-colors duration-200 ${
      active ? 'text-blue-600' : 'text-slate-600 hover:text-blue-600'
    }`;

  // Logic to determine mobile title based on the route
  const getMobileTitle = () => {
    if (location.pathname.includes('/qrcode-generator')) return t.nav.generator;
    if (location.pathname.includes('/text-to-qrcode')) return t.pageTitles.textToQr;
    if (location.pathname.includes('/link-to-qrcode')) return t.pageTitles.linkToQr;
    if (location.pathname.includes('/qrcode-scanner')) return t.nav.scanner;
    return null;
  };

  const mobileTitle = getMobileTitle();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && hideMobileChrome) {
        setHideMobileChrome(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [hideMobileChrome]);

  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth >= 768) {
        lastScrollY.current = window.scrollY;
        return;
      }
      const current = window.scrollY;
      const diff = current - lastScrollY.current;
      if (current < 10) {
        setHideMobileChrome(false);
      } else if (diff > 6) {
        setHideMobileChrome(true);
      }
      lastScrollY.current = current;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-200 selection:text-blue-900">
      {/* Premium Header - Optimized height for mobile */}
      <header className={`sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm supports-[backdrop-filter]:bg-white/60 transition-transform duration-300 ${hideMobileChrome ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Changed height from h-20 to h-14 for mobile, kept h-20 for desktop */}
          <div className="flex items-center h-14 md:h-20 gap-8">
            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer group flex-shrink-0" 
              onClick={() => navigate(`/${currentLang}/`)}
            >
              {/* Scaled down logo slightly for mobile */}
              <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white font-bold text-xl md:text-2xl shadow-lg shadow-blue-500/30 group-hover:scale-105 transition-transform duration-300">
                Y
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg md:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 tracking-tight">
                  YesQRCode
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-[9px] sm:text-[10px] font-bold border border-green-200 uppercase tracking-wide">
                  {t.common.free}
                </span>
              </div>
            </div>

            {/* Desktop Nav - Left Aligned */}
            <nav className="hidden md:flex items-center gap-8">
                <NavLink
                  to={`/${currentLang}/qrcode-generator`}
                  className={({ isActive }) => navClass(isActive || isGeneratorRoute)}
                >
                  {t.nav.generator}
                </NavLink>
                <NavLink
                  to={`/${currentLang}/qrcode-scanner`}
                  className={({ isActive }) => navClass(isActive || isScannerRoute)}
                >
                  {t.nav.scanner}
                </NavLink>
                <button
                  type="button"
                  onClick={() => setShowMobileQR(true)}
                  className={navClass(false)}
                >
                  {t.nav.mobile}
                </button>
                <NavLink
                  to={`/${currentLang}/blog`}
                  className={({ isActive }) => navClass(isActive)}
                >
                  {t.nav.blog}
                </NavLink>
            </nav>

            {/* Language Switcher & Mobile Menu - Pushed to Right */}
            <div className="ml-auto flex items-center gap-4">
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-all text-slate-600 font-medium text-base"
                >
                  <Globe size={18} />
                  <span>{LANGUAGES[currentLang].name}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLangOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsLangOpen(false)}></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-20">
                      <div className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50/50 border-b border-slate-50">
                        {t.nav.langSelect}
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {Object.entries(LANGUAGES).map(([code, data]) => (
                          <button
                            key={code}
                            onClick={() => handleLangChange(code as LanguageCode)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-lg transition-colors ${
                              currentLang === code 
                              ? 'bg-blue-50 text-blue-600 font-medium' 
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            <span className="text-lg">{data.flag}</span>
                            <span>{data.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Top position adjusted for new header height */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-14 left-0 w-full bg-white border-b border-slate-200 shadow-xl z-40 animate-in slide-in-from-top-5">
            <div className="px-4 py-6 space-y-4">
              <div className="space-y-2">
                <NavLink
                  to={`/${currentLang}/qrcode-generator`}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl text-base font-medium transition-all ${
                      isActive || isGeneratorRoute
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  {t.nav.generator}
                </NavLink>
                <NavLink
                  to={`/${currentLang}/qrcode-scanner`}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl text-base font-medium transition-all ${
                      isActive || isScannerRoute
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  {t.nav.scanner}
                </NavLink>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMobileQR(true);
                    setIsMenuOpen(false);
                  }}
                  className="block px-4 py-3 rounded-xl text-base font-medium transition-all text-slate-600 hover:bg-slate-50"
                >
                  {t.nav.mobile}
                </Link>
                <NavLink
                  to={`/${currentLang}/blog`}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  {t.nav.blog}
                </NavLink>
              </div>
              
              <div className="pt-6 border-t border-slate-100">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-2">{t.nav.langSelect}</div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(LANGUAGES).map(([code, data]) => (
                    <button
                      key={code}
                      onClick={() => handleLangChange(code as LanguageCode)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm transition-all border ${
                        currentLang === code 
                        ? 'border-blue-200 bg-blue-50 text-blue-700 font-semibold shadow-sm' 
                        : 'border-slate-100 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span>{data.flag}</span> {data.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Page Title Bar */}
      {mobileTitle && (
        <div
          className={`md:hidden bg-white border-b border-slate-200 py-3 px-4 shadow-sm sticky z-30 transition-transform duration-300 ${hideMobileChrome ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}
          style={{ top: hideMobileChrome ? 0 : '3.5rem' }}
        >
          <h2 className="text-center font-bold text-slate-800 text-sm">{mobileTitle}</h2>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow">
        <div className="w-full">
          <Suspense
            fallback={
              <div className="min-h-[40vh] flex items-center justify-center px-4 py-12">
                <div className="text-slate-500 font-medium">Loading…</div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Premium Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto pt-12 pb-8">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                 <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-lg">Y</div>
                 <span className="text-xl font-bold text-slate-900">YesQRCode</span>
              </div>
              <p className="text-slate-500 text-base leading-relaxed mb-6">
                {t.common.footerDesc}
              </p>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 cursor-pointer transition-colors"><Github size={16} /></div>
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 cursor-pointer transition-colors"><Twitter size={16} /></div>
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 cursor-pointer transition-colors"><Linkedin size={16} /></div>
              </div>
            </div>
            
            <div>
              <h2 className="font-bold text-slate-900 mb-6 text-lg">{t.common.product}</h2>
              <ul className="space-y-3 text-base text-slate-500">
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/qrcode-generator`}>{t.nav.generator}</Link>
                </li>
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/qrcode-scanner`}>{t.nav.scanner}</Link>
                </li>
                <li className="hover:text-blue-600 cursor-pointer transition-colors">{t.common.api}</li>
              </ul>
            </div>
            
            <div>
              <h2 className="font-bold text-slate-900 mb-6 text-lg">{t.common.resources}</h2>
              <ul className="space-y-3 text-base text-slate-500">
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/blog`}>{t.nav.blog}</Link>
                </li>
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/help`}>{t.common.help}</Link>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-bold text-slate-900 mb-6 text-lg">{t.common.company}</h2>
              <ul className="space-y-3 text-base text-slate-500">
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/contact`}>{t.common.contact}</Link>
                </li>
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/privacy`}>{t.common.privacy}</Link>
                </li>
                <li className="hover:text-blue-600 cursor-pointer transition-colors">
                    <Link to={`/${currentLang}/terms`}>{t.common.terms}</Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Language Switcher Section in Footer */}
          <div className="border-t border-slate-100 pt-8 pb-8">
            <h2 className="text-base font-bold text-slate-900 mb-4">{t.nav.langSelect}</h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {Object.entries(LANGUAGES).map(([code, data]) => (
                <button
                  key={code}
                  onClick={() => handleLangChange(code as LanguageCode)}
                  className={`text-base flex items-center gap-2 transition-colors ${
                    currentLang === code 
                    ? 'text-blue-600 font-semibold' 
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <span>{data.flag}</span>
                  <span>{data.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="border-t border-slate-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-base font-medium">{t.common.footer}</p>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
              <div className="w-2 h-2 rounded-full bg-green-50 animate-pulse"></div>
              {t.common.systemStatus}
            </div>
          </div>
        </div>
      </footer>

      {showMobileQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowMobileQR(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 z-10">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{t.nav.mobile}</p>
                <p className="text-xl font-bold text-slate-900">{t.common.scanToOpen}</p>
                <p className="text-sm text-slate-500 mt-1 break-all">{mobileQrUrl}</p>
              </div>
              <button
                className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
                onClick={() => setShowMobileQR(false)}
                type="button"
                aria-label="Close dialog"
              >
                <X size={18} />
              </button>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 flex justify-center">
              <QRCodeSVG value={mobileQrUrl} size={200} bgColor="#F8FAFC" fgColor="#0F172A" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
